import { useMemo } from 'react';
import type { Post } from '../types';
import { PLATFORM_NAMES } from '../constants';

interface PostCardProps {
  post: Post;
  compact?: boolean;
}

export function PostCard({ post, compact = false }: PostCardProps) {
  const { formattedDate, formattedTime } = useMemo(() => {
    const dateTime = new Date(`${post.date}T${post.time}`);
    return {
      formattedDate: dateTime.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      }),
      formattedTime: dateTime.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      }),
    };
  }, [post.date, post.time]);

  return (
    <div className="space-y-2">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 text-sm">
            <span className="font-medium text-foreground">
              {formattedDate}, {formattedTime}
            </span>
            <span className="text-muted-foreground">•</span>
            <span className="text-muted-foreground capitalize">
              {PLATFORM_NAMES[post.platform]}
            </span>
          </div>
          {post.topic && (
            <div className="font-medium text-foreground mt-1">{post.topic}</div>
          )}
        </div>

        {/* Show thumbnail in compact mode */}
        {compact && post.attachments?.some(a => a.type === 'image') && (
          <div className="flex-shrink-0">
            <img
              src={post.attachments.find(a => a.type === 'image')!.url}
              alt=""
              className="w-12 h-12 object-cover rounded"
            />
          </div>
        )}
      </div>

      {!compact && (
        <>
          <p className="text-sm text-foreground line-clamp-3">{post.text}</p>

          {post.attachments?.some(a => a.type === 'image') && (
            <div className="flex gap-2">
              {post.attachments.filter(a => a.type === 'image').slice(0, 3).map((attachment, idx) => (
                <img
                  key={idx}
                  src={attachment.url}
                  alt=""
                  className="w-16 h-16 object-cover rounded"
                />
              ))}
              {post.attachments.filter(a => a.type === 'image').length > 3 && (
                <div className="w-16 h-16 bg-muted rounded flex items-center justify-center text-xs text-muted-foreground">
                  +{post.attachments.filter(a => a.type === 'image').length - 3}
                </div>
              )}
            </div>
          )}

          {post.attachments?.some(a => a.type === 'video') && (
            <div className="text-sm text-muted-foreground">📹 Video attached</div>
          )}
        </>
      )}
    </div>
  );
}
